import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Refresh as RefreshIcon } from "@material-ui/icons";
import { Button, CircularProgress, Grid, TextField } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import SaveIcon from "@material-ui/icons/Save";
import { Alert } from "@material-ui/lab";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridValueFormatterParams
} from "@mui/x-data-grid";
import { CollapsiblePanel, ConfirmDialog } from "src/apps/commons/dataDisplays";
import { AsyncDropdown } from "src/apps/commons/dropdowns";
import { listAllPartners } from "src/apps/commons/dropdowns/api";
import { FormPaper } from "src/apps/commons/papers";
import { IPartnerBrief } from "src/apps/modules/partner/types";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { formatDateTime } from "src/utils";
import * as Api from "../api";
import { ISerialNoMappingItem, ISerialNoUploadItem } from "../types";

const useStyles = makeStyles(theme => ({
  ul: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
    "&> ul": {
      marginTop: 4,
      marginBottom: 4
    }
  }
}));

const DataTable = (props: {
  data: ISerialNoMappingItem[];
  loading: boolean;
}) => {
  const { data, loading } = props;

  const [pageSize, setPageSize] = useState(10);
  const itemsWithId = useMemo(
    () =>
      data.map((it, idx) => ({
        ...it,
        id: idx
      })),
    [data]
  );
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "serialNo",
        headerName: "Serial No.",
        flex: 0.5
      },
      {
        field: "refProductCode",
        headerName: "Ref Product Code",
        flex: 0.5
      },
      {
        field: "createdAt",
        headerName: "Created At",
        align: "center",
        width: 200,
        valueFormatter: (params: GridValueFormatterParams) => {
          return formatDateTime(params.value as string);
        }
      }
    ],
    []
  );

  return (
    <div>
      <DataGrid
        rows={itemsWithId}
        loading={loading}
        columns={columns}
        density="compact"
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 50, 100]}
        disableSelectionOnClick
        disableColumnMenu
        autoHeight
        components={{
          Toolbar: GridToolbar
        }}
      />
    </div>
  );
};

const SerialNoList = () => {
  const { handleError } = useHttpErrorHandler();

  const [partner, setPartner] = useState<IDropdown<IPartnerBrief> | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] =
    useState<ISerialNoMappingItem[] | undefined>(undefined);

  const fetchData = useCallback(async () => {
    if (!partner) {
      setData(undefined);
      return;
    }

    try {
      setLoading(true);
      const resp = await Api.listSerialNo(partner.value);
      setData(resp.value);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  }, [partner, handleError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return (
    <CollapsiblePanel
      title="แสดงข้อมูล Serial No. Mapping ในระบบ"
      icon="search"
      defaultCollapse
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <AsyncDropdown
            label={"Partner *"}
            fetchDropdown={listAllPartners}
            paginate={{
              page: 1,
              pageSize: 500,
              order: {
                optionKey: "NAME",
                desc: false
              }
            }}
            value={partner}
            onChange={setPartner}
            disabled={loading}
          />
        </Grid>

        {data && (
          <>
            <Grid item xs={12}>
              <Button
                variant="contained"
                type="button"
                startIcon={
                  loading ? (
                    <CircularProgress color="secondary" size={20} />
                  ) : (
                    <RefreshIcon />
                  )
                }
                disabled={loading}
                onClick={fetchData}
              >
                Reload
              </Button>
            </Grid>
            <Grid item xs={12}>
              <DataTable data={data} loading={loading} />
            </Grid>
          </>
        )}
      </Grid>
    </CollapsiblePanel>
  );
};

const parseText = (text: string): ISerialNoUploadItem[] => {
  const sep = new RegExp("[\\s\\t]+");
  return text
    .split("\n")
    .map(it => it.trim())
    .filter(it => !!it)
    .map(it => it.split(sep))
    .filter(it => it.length >= 1 && it[0])
    .map(it => ({
      serialNo: it[0],
      refProductCode: it.length > 1 ? it[1] : ""
    }));
};

const placeholderText = `
SERIAL_NO_1  REF_PRODUCT_CODE_1
SERIAL_NO_2  REF_PRODUCT_CODE_2
SERIAL_NO_3  REF_PRODUCT_CODE_3
SERIAL_NO_4  REF_PRODUCT_CODE_4

SERIAL_NO_5  REF_PRODUCT_CODE_5A
SERIAL_NO_5  REF_PRODUCT_CODE_5B
SERIAL_NO_5  REF_PRODUCT_CODE_5C

SERIAL_NO_TO_DELETE_1
SERIAL_NO_TO_DELETE_2

...
`.trim();

const UploadSSW: React.FC = _ => {
  const classes = useStyles();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [partner, setPartner] = useState<IDropdown<IPartnerBrief> | null>(null);
  const [text, setText] = React.useState("");

  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setText(event.target.value);
    if (imported) {
      setImported(false);
    }
  };

  const [confirmDialogState, setConfirmDialogState] = useState<{
    open: boolean;
    message?: string;
    onConfirm?: () => void;
  }>({ open: false });
  const handleCloseDialog = () => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  };
  const handleUploadImport = () => {
    if (!partner) {
      setMessage("error", "กรุณาเลือก Partner");
      return;
    }
    const items = parseText(text);
    if (items.length === 0) {
      setMessage("error", "กรุณากรอก Serial No. & Ref Product Code");
      return;
    }

    const insertCount = items.filter(it => it.refProductCode).length;
    const deleteCount = items.filter(it => !it.refProductCode).length;

    setConfirmDialogState({
      open: true,
      message: `Insert ${insertCount} บรรทัด\nDelete ${deleteCount} บรรทัด`,
      onConfirm: async () => {
        if (importing) return;

        try {
          setImporting(true);
          await Api.uploadSerialNo({
            partnerId: partner.value,
            items: items
          });

          setMessage("success", `Import Serial No. สำเร็จ`);
          setImported(true);
        } catch (e) {
          handleError(e);
        } finally {
          setImporting(false);
          handleCloseDialog();
        }
      }
    });
  };

  return (
    <>
      <FormPaper>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AsyncDropdown
              label={"Partner *"}
              fetchDropdown={listAllPartners}
              paginate={{
                page: 1,
                pageSize: 500,
                order: {
                  optionKey: "NAME",
                  desc: false
                }
              }}
              value={partner}
              onChange={setPartner}
              disabled={importing}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="Serial No. & Ref Product Code *"
              placeholder={placeholderText}
              value={text}
              onChange={handleChange}
              multiline
              rows={20}
              rowsMax={100}
              disabled={importing}
              inputProps={{ style: { fontFamily: "monospace" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              หลักการนำเข้าประกอบไปด้วย:
              <ul className={classes.ul}>
                <li>
                  กรอก Serial No. และ Ref Product Code (คั่นด้วย space หรือ tab)
                  คู่ละ 1 บรรทัด
                </li>
                <li>
                  กรอก Serial No. อย่างเดียว หากต้องการลบ Serial No.
                  นั้นออกจากระบบ
                </li>
                <li>1 Serial No. สามารถคู่กับหลาย Ref Product Code ได้</li>
                <li>
                  เมื่อกด Import ระบบจะลบ Mapping เดิมที่มีอยู่ ของ Serial No.
                  ที่กรอกมาทั้งหมด แล้วค่อยเอาเข้าใหม่ตามที่กรอก
                </li>
              </ul>
            </Alert>
          </Grid>
        </Grid>

        <Grid container spacing={2} justify="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              type="button"
              startIcon={
                importing ? (
                  <CircularProgress color="secondary" size={20} />
                ) : imported ? (
                  <CheckIcon />
                ) : (
                  <SaveIcon />
                )
              }
              disabled={importing || imported}
              onClick={handleUploadImport}
            >
              Import Serial No.
            </Button>
          </Grid>
        </Grid>

        <ConfirmDialog
          title={`ยืนยันการ Import ข้อมูล Serial No.`}
          description={confirmDialogState.message}
          open={confirmDialogState.open}
          onClose={handleCloseDialog}
          onConfirm={confirmDialogState.onConfirm}
          confirming={importing}
        />
      </FormPaper>
      <SerialNoList />
    </>
  );
};

export default UploadSSW;
